/**
 * ai-chat.store.ts — shared TruInsight AI conversation state.
 *
 * The assistant renders on two surfaces (the dashboard `nl-query` widget and the global `AppShell`
 * floating action button). Holding the conversation in a single Zustand store — rather than a
 * per-mount `useState` — makes those surfaces ONE live conversation: a question asked from the FAB
 * is instantly visible in the dashboard widget and vice-versa. Persistence to the user-preferences
 * blob is unchanged (single thread under `nlConversation`, capped at the last 50 messages).
 *
 * Scope is resolved server-side from the caller's role (see `ai.service.ts#resolveAiScope`): SSI
 * users answer across ALL companies, or the active dashboard lens (company / building / view-as-user);
 * everyone else is pinned to their own company. No company picker lives in the chat UI.
 */
import { create } from 'zustand';
import { userPreferencesApi, type UserPreferences } from '@/api/endpoints/user-preferences';
import { sanitizeAiCards, sanitizeAiLinks } from '@/features/ai-assistant/lib/ai-cards';
import type { AiChatScopeRequest } from '@/features/ai-assistant/lib/ai-chat-scope';
import type { AiCard, ChatMessage } from '@/features/ai-assistant/types';
import { useAuthStore } from '@/stores/auth.store';

/** One Server-Sent Event from POST /api/ai/query/stream (mirrors `AiStreamEvent` on the server). */
type AiStreamEvent =
  | {
      type: 'meta';
      sqlQuery: string | null;
      data: Record<string, unknown>[] | null;
      error: string | null;
      cards?: AiCard[];
    }
  | { type: 'cards'; cards: AiCard[]; links?: { label: string; href: string }[] }
  | { type: 'delta'; text: string }
  | { type: 'done' }
  | { type: 'error'; message: string };

const PREFS_KEY = 'nlConversation';
const MAX_PERSISTED_MESSAGES = 50;
/** Trailing conversation turns sent to the model for context (6 exchanges). */
const HISTORY_MESSAGES = 12;

const HTTP_TOO_MANY_REQUESTS = 429;
const RATE_LIMIT_FALLBACK = 'You have asked a lot of questions in the last minute. Please wait a moment and try again.';

/** The server's 429 sentence (`{ error: { message } }`), or a default when the body is not JSON. */
async function rateLimitMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { message?: unknown } };
    return typeof body.error?.message === 'string' ? body.error.message : RATE_LIMIT_FALLBACK;
  } catch {
    return RATE_LIMIT_FALLBACK;
  }
}

let messageCounter = 0;
function nextId(): string {
  return `msg_${Date.now()}_${++messageCounter}`;
}

interface PersistedConversation {
  v: 1;
  updatedAt: number;
  messages: ChatMessage[];
}

/** Coerce a persisted blob back into a clean message list, dropping anything malformed. */
function normalizePersisted(raw: unknown): ChatMessage[] | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<PersistedConversation>;
  if (!Array.isArray(r.messages)) return null;
  const out: ChatMessage[] = [];
  for (const m of r.messages) {
    if (!m || typeof m !== 'object') continue;
    const role = (m as ChatMessage).role;
    const content = (m as ChatMessage).content;
    if (typeof content !== 'string') continue;
    if (role !== 'user' && role !== 'assistant' && role !== 'system') continue;
    out.push({
      id: typeof (m as ChatMessage).id === 'string' ? (m as ChatMessage).id : nextId(),
      role,
      content,
      timestamp: typeof (m as ChatMessage).timestamp === 'number' ? (m as ChatMessage).timestamp : Date.now(),
      sqlQuery: (m as ChatMessage).sqlQuery,
      data: (m as ChatMessage).data,
      error: (m as ChatMessage).error,
      cards: sanitizeAiCards((m as ChatMessage).cards),
      links: sanitizeAiLinks((m as ChatMessage).links),
    });
  }
  return out.length ? out : null;
}

/** Persist the current thread (minus the in-flight loading bubble). Fire-and-forget. */
function persistConversation(messages: ChatMessage[]): void {
  const userId = useAuthStore.getState().user?.userId ?? 0;
  if (userId <= 0) return;
  const persistable = messages.filter((m) => !m.isLoading).slice(-MAX_PERSISTED_MESSAGES);
  const payload: PersistedConversation = { v: 1, updatedAt: Date.now(), messages: persistable };
  userPreferencesApi
    .patch(userId, { [PREFS_KEY]: payload as unknown as UserPreferences[string] })
    .catch(() => {
      /* swallow — next exchange retries */
    });
}

interface AiChatState {
  messages: ChatMessage[];
  isLoading: boolean;
  /** The user id the current thread was hydrated for; guards one-time hydration per user. */
  hydratedUserId: number | null;
  /** Load the persisted thread once per user; falls back to `welcome` when none exists. */
  hydrate: (userId: number, welcome: ChatMessage) => Promise<void>;
  /**
   * Rewrite the welcome bubble when the SSI lens changes (DEF-269). No-op when the thread has
   * moved past a lone welcome (so we never clobber an in-flight conversation).
   */
  replaceWelcome: (welcome: ChatMessage) => void;
  /** Send a question and stream the answer. Scope overrides narrow an SSI caller to the dashboard lens. */
  sendMessage: (question: string, opts: AiChatScopeRequest) => Promise<void>;
  /** Reset to a single personalized welcome bubble and persist the empty thread. */
  clearHistory: (welcome: ChatMessage) => void;
}

export const useAiChatStore = create<AiChatState>((set, get) => ({
  messages: [],
  isLoading: false,
  hydratedUserId: null,

  hydrate: async (userId, welcome) => {
    if (userId <= 0) return;
    if (get().hydratedUserId === userId) return;
    // Claim hydration synchronously so a second mounting surface doesn't double-fetch.
    set({ hydratedUserId: userId, messages: get().messages.length ? get().messages : [welcome] });
    try {
      const blob = await userPreferencesApi.get(userId);
      const persisted = normalizePersisted(blob[PREFS_KEY]);
      // Only replace the seeded welcome if the user hasn't started chatting since hydration began.
      const current = get().messages;
      const untouched = current.length <= 1 && current[0]?.id === welcome.id;
      if (persisted && untouched) set({ messages: persisted });
    } catch {
      /* keep the welcome message on failure */
    }
  },

  replaceWelcome: (welcome) => {
    const current = get().messages;
    // Only touch a pristine welcome thread — never rewrite after the user has asked a question.
    if (current.length === 1 && current[0]?.id === 'welcome') {
      set({ messages: [welcome] });
    } else if (current.length === 0) {
      set({ messages: [welcome] });
    }
  },

  sendMessage: async (question, { scopeCompanyId, scopeBuildingId, scopeUserId }) => {
    const userMsg: ChatMessage = { id: nextId(), role: 'user', content: question, timestamp: Date.now() };
    const loadingMsg: ChatMessage = {
      id: nextId(),
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      isLoading: true,
    };

    const priorMessages = get().messages;
    set({ messages: [...priorMessages, userMsg, loadingMsg], isLoading: true });

    // Last 6 exchanges for context (skip the welcome + any system rows).
    const history = priorMessages
      .filter((m) => m.role !== 'system' && m.id !== 'welcome')
      .slice(-HISTORY_MESSAGES)
      .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));

    let answer = '';
    let sqlQuery: string | undefined;
    let data: Record<string, unknown>[] | null | undefined;
    let cards: AiCard[] | undefined;
    let links: { label: string; href: string }[] | undefined;
    let errorText: string | undefined;

    const apply = () => {
      set((state) => ({
        messages: state.messages.map((m) =>
          m.id === loadingMsg.id
            ? {
                ...m,
                role: 'assistant',
                content: answer,
                // Keep the "Analyzing…" spinner until the first token (or an error) arrives.
                isLoading: answer.length === 0 && !errorText,
                sqlQuery,
                data: data ?? undefined,
                cards,
                links,
                error: errorText,
                timestamp: Date.now(),
              }
            : m,
        ),
      }));
    };

    try {
      const token = useAuthStore.getState().accessToken;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      };
      if (token) headers.Authorization = `Bearer ${token}`;
      const apiSecret = import.meta.env.VITE_API_SECRET;
      if (apiSecret) headers['x-api-secret'] = apiSecret;

      // Stream directly with fetch — axios buffers the whole body, defeating token streaming.
      const res = await fetch('/api/ai/query/stream', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          question,
          conversationHistory: history,
          scopeCompanyId,
          scopeBuildingId,
          scopeUserId,
        }),
      });
      if (res.status === HTTP_TOO_MANY_REQUESTS) {
        answer = await rateLimitMessage(res);
        apply();
        return;
      }
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const handleFrame = (frame: string) => {
        const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
        if (!dataLine) return;
        const payload = dataLine.slice('data:'.length).trim();
        if (!payload || payload === '[DONE]') return;
        let event: AiStreamEvent;
        try {
          event = JSON.parse(payload) as AiStreamEvent;
        } catch {
          return;
        }
        if (event.type === 'meta') {
          sqlQuery = event.sqlQuery ?? undefined;
          data = event.data;
          cards = sanitizeAiCards(event.cards);
          if (event.error) errorText = event.error;
        } else if (event.type === 'cards') {
          cards = sanitizeAiCards(event.cards);
          links = sanitizeAiLinks(event.links);
        } else if (event.type === 'delta') {
          answer += event.text;
        } else if (event.type === 'error') {
          errorText = event.message;
        }
        apply();
      };

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep: number;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          handleFrame(frame);
        }
      }
      if (buffer.trim()) handleFrame(buffer);

      if (!answer && !errorText) {
        answer = "I wasn't able to generate an answer. Please try rephrasing your question.";
      }
      apply();
    } catch (err) {
      errorText = err instanceof Error ? err.message : 'Unknown error';
      if (!answer) {
        answer = 'Sorry, I encountered an error processing your request. Please try rephrasing your question.';
      }
      apply();
    } finally {
      set({ isLoading: false });
      persistConversation(get().messages);
    }
  },

  clearHistory: (welcome) => {
    set({ messages: [welcome] });
    persistConversation(get().messages);
  },
}));
